import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import HeaderV2 from '../components/HeaderV2'
import StoreHeroV2 from './store/v2/StoreHeroV2'
import StoreCardV2 from './store/v2/StoreCardV2'
import type { StoreV2, Lang } from './store/v2/types'
import { lumi } from '../lib/lumi'

// Default fallback stores (used when collection has no data)
const DEFAULT_STORES: StoreV2[] = [
  {
    _id: 'xinyi-flagship',
    name: 'ONGU Studio 信義店',
    nameEn: 'ONGU Studio Xinyi',
    slug: 'xinyi',
    address: { full: '110臺北市信義區信義路四段389號1樓' },
    images: [
      'https://static.lumi.new/43/436fdf374d21ad86da042379ba0813b9.webp',
    ],
    businessHours: { monday: '09:00–17:00' },
    hours: '週一至五 09:00–17:00｜週六 10:00–17:00｜週日公休',
    phone: '+886-2-2345-6789',
    googleMapsUrl: 'https://share.google/71U8llxf43jTzpGjC',
    status: 'active',
  },
  {
    _id: 'renai-branch',
    name: 'Branch Coffee Roasters 仁愛店',
    nameEn: 'Branch Coffee Roasters Renai',
    slug: 'renai',
    address: { full: '台北市大安區仁愛路四段345巷15弄2號1樓' },
    images: [
      'https://images.pexels.com/photos/2074130/pexels-photo-2074130.jpeg?auto=compress&cs=tinysrgb&w=1600',
    ],
    businessHours: { monday: '10:00–17:00' },
    hours: '週三至週一 10:00–17:00（週二公休）',
    googleMapsUrl: 'https://maps.app.goo.gl/wjpdZX1dWCxTDsLJ8',
    status: 'coming_soon',
  },
]

const StoreV2Page: React.FC = () => {
  const location = useLocation()
  const lang: Lang = location.pathname.startsWith('/en') ? 'en' : 'zh'
  const isPreviewRoute = location.pathname.includes('/v2')

  const [stores, setStores] = useState<StoreV2[]>([])
  const [loading, setLoading] = useState(true)

  // Inject Barista Map fonts once
  useEffect(() => {
    const ID = 'ongu-v2-fonts'
    if (document.getElementById(ID)) return
    const link = document.createElement('link')
    link.id = ID
    link.rel = 'stylesheet'
    link.href =
      'https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Noto+Serif+TC:wght@300;400;500&display=swap'
    document.head.appendChild(link)
  }, [])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        const res = await lumi.entities.stores.list({
          // SAMPLE: showing all stores — toggle to { status: 'active' } for production
          sort: { order: 1, createdAt: 1 },
          limit: 50,
        })
        if (cancelled) return
        const list: StoreV2[] = res?.list || []
        setStores(list.length > 0 ? list : DEFAULT_STORES)
      } catch (err) {
        console.error('[StoreV2Page] fetch failed:', err)
        if (!cancelled) setStores(DEFAULT_STORES)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [])

  const seo = useMemo(
    () =>
      lang === 'zh'
        ? {
            title: 'ONGU Studio 門市｜實體咖啡空間 (V2 預覽)',
            description:
              'ONGU Studio 實體門市總覽——信義旗艦店與合作分店。職人手沖、現場烘豆，歡迎走進咖啡的真實樣貌。',
          }
        : {
            title: 'ONGU Studio Stores｜Coffee Spaces (V2 Preview)',
            description:
              'Discover ONGU Studio physical stores — the Xinyi flagship and partner branches. On-site roasting and hand-brew experiences await.',
          },
    [lang]
  )

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name='description' content={seo.description} />
        {isPreviewRoute && <meta name='robots' content='noindex,nofollow' />}
        {isPreviewRoute && (
          <link rel='canonical' href={`https://www.ongucoffee.com/${lang}/store`} />
        )}
      </Helmet>

      <div
        className='bg-white text-black min-h-screen'
        style={{ fontFamily: "'Noto Sans TC', 'Helvetica Neue', sans-serif" }}
      >
        {/* Solid header (white bg, black text) — Barista Map style */}
        <HeaderV2 />

        {/* Spacer for fixed navbar height (h-20 = 80px) */}
        <div className='h-20' />

        <StoreHeroV2 lang={lang} />

        {/* Stores dual-column grid */}
        <section className='py-16 md:py-24'>
          <div className='mx-auto max-w-[1600px] px-6 md:px-10'>
            {loading ? (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-16'>
                {[0, 1].map(i => (
                  <div key={i}>
                    <div className='aspect-square w-full bg-black/[0.04]' />
                    <div className='pt-6 space-y-3'>
                      <div className='h-6 w-2/3 bg-black/[0.05]' />
                      <div className='h-4 w-3/4 bg-black/[0.04]' />
                      <div className='h-4 w-1/2 bg-black/[0.04]' />
                    </div>
                  </div>
                ))}
              </div>
            ) : stores.length === 0 ? (
              <p className='text-sm text-black/60'>
                {lang === 'zh' ? '目前尚無門市資料' : 'No stores available yet.'}
              </p>
            ) : (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-x-10 md:gap-x-16 gap-y-20'>
                {stores.map(store => (
                  <StoreCardV2
                    key={store._id}
                    store={store}
                    lang={lang}
                    isComingSoon={store.status === 'coming_soon'}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  )
}

export default StoreV2Page
